import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@satyrsmc/shared/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useEventsTable, useInvalidateQueries, useUrlSearchSync } from "@/queries/hooks";
import { EVENT_TYPES, EVENT_TYPE_LABELS } from "@/lib/event-constants";
import { BarChart3, Plus, Search, X, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { AddEventDialog } from "./AddEventDialog";
import { EventType } from "@satyrsmc/shared/client";
import { formatDate } from "@/lib/date-utils";

const PER_PAGE = 20;

export function EventsPanel() {
  const navigate = useNavigate();
  const invalidate = useInvalidateQueries();
  const [addOpen, setAddOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const rawType = searchParams.get("type") ?? "all";
  const upcoming = searchParams.get("upcoming") !== "false";
  const dateFrom = searchParams.get("date_from") ?? "";
  const dateTo = searchParams.get("date_to") ?? "";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const { search, setSearch } = useUrlSearchSync(searchParams, setSearchParams);
  const rawQ = searchParams.get("q") ?? "";

  function setParam(key: string, value: string | null) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value === "") next.delete(key);
        else next.set(key, value);
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  function setPage(p: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p === 1) next.delete("page");
        else next.set("page", String(p));
        return next;
      },
      { replace: true },
    );
  }

  function clearFilters() {
    setSearch("");
    setSearchParams({}, { replace: true });
  }

  const input = {
    type: rawType !== "all" ? (rawType as EventType) : undefined,
    search: rawQ || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    upcoming,
    page,
    per_page: PER_PAGE,
  };

  const { data, isLoading } = useEventsTable(input);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const hasActiveFilters = rawQ !== "" || rawType !== "all" || dateFrom !== "" || dateTo !== "";

  const handleCreateSuccess = (eventId: string) => {
    invalidate.invalidateEvents();
    navigate(`/admin/events/${eventId}`);
  };

  return (
    <div className="space-y-6">
      {rawType === "rides" && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="size-12 text-muted-foreground/50 mb-4" />
            <p className="text-center text-muted-foreground">
              Historical attendance graphs will be available here.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Events</h1>
          <p className="text-muted-foreground mt-1">
            Club events across years. Click an event to view details, plan milestones, manage
            volunteers, and more.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="size-4 mr-2" />
          Create event
        </Button>
      </div>

      <AddEventDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSuccess={handleCreateSuccess}
        defaultEventType={rawType !== "all" ? (rawType as EventType) : EventType.Badger}
      />

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Search */}
        <div className="relative w-full sm:w-60">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Event type */}
        <Select value={rawType} onValueChange={(v) => setParam("type", v === "all" ? null : v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Event Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {EVENT_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {EVENT_TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date range */}
        <DatePicker
          value={dateFrom}
          onChange={(v) => setParam("date_from", v || null)}
          placeholder="From date"
          className="w-40"
        />
        <DatePicker
          value={dateTo}
          onChange={(v) => setParam("date_to", v || null)}
          placeholder="To date"
          className="w-40"
        />

        {/* Upcoming / Past toggle */}
        <div className="flex items-center gap-2">
          <Switch
            id="upcoming-toggle"
            checked={upcoming}
            onCheckedChange={(v) => setParam("upcoming", v ? null : "false")}
          />
          <Label htmlFor="upcoming-toggle" className="text-sm cursor-pointer">
            {upcoming ? "Upcoming" : "Past"}
          </Label>
        </div>

        {/* Clear filters */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
            <X className="size-3.5" />
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-4 py-3">Name</TableHead>
              <TableHead className="px-4 py-3">Type</TableHead>
              <TableHead className="px-4 py-3">Start Date</TableHead>
              <TableHead className="px-4 py-3">End Date</TableHead>
              <TableHead className="px-4 py-3"># of Attendees</TableHead>
              <TableHead className="px-4 py-3 text-center">Members Only</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              (["s0", "s1", "s2", "s3", "s4"] as const).map((k) => (
                <TableRow key={k}>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-40 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-10 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="mx-auto h-4 w-4 animate-pulse rounded bg-muted" />
                  </TableCell>
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  {hasActiveFilters
                    ? "No events match your filters."
                    : upcoming
                      ? "No upcoming events. Create an event to get started."
                      : "No past events found."}
                </TableCell>
              </TableRow>
            ) : (
              items.map((e) => (
                <TableRow
                  key={e.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/admin/events/${e.id}`)}
                >
                  <TableCell className="px-4 py-3 font-medium">{e.name}</TableCell>
                  <TableCell className="px-4 py-3 text-muted-foreground">
                    {EVENT_TYPE_LABELS[e.event_type]}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-muted-foreground">
                    {e.start_date ? formatDate(e.start_date) : "—"}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-muted-foreground">
                    {e.end_date ? formatDate(e.end_date) : "—"}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-muted-foreground">
                    {e.attendee_count}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-center">
                    {e.members_only && <Check className="mx-auto size-4" />}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="size-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
